<template>
  <f7-page name="shared-cart-page">
    <f7-navbar :title="$t('sharedCart.title')" back-link />
    <AppLayout>
      <template #main>
        <f7-block v-if="loading"><f7-preloader /></f7-block>
        <f7-block v-else-if="error">{{ error }}</f7-block>
        <template v-else>
          <f7-block strong inset class="cart-summary-block">
            <h2>{{ cart.title || $t("sharedCart.title") }}</h2>
            <p>
              {{ $t("sharedCart.participants", { count: participantsCount }) }}
            </p>
            <f7-progressbar :progress="cart.progress || 50" />
            <p class="remaining-text">{{ cart.remainingText }}</p>
          </f7-block>

          <f7-block-title>{{
            $t("common.items") || "Items in Cart"
          }}</f7-block-title>
          <f7-list strong inset media-list>
            <f7-list-item
              v-for="item in cartItems"
              :key="item.id"
              :title="item.productName"
              :subtitle="`${item.variantName || ''} × ${item.quantity}`"
              :after="
                formatMoney(
                  item.totalAmount || item.currentUnitPrice * item.quantity,
                )
              "
            />
          </f7-list>

          <f7-block-title>{{
            $t("common.participants") || "Participants"
          }}</f7-block-title>
          <f7-list strong inset media-list>
            <f7-list-item
              v-for="participant in cart.participants || []"
              :key="participant.id"
              :title="participant.displayName"
            >
              <template #after>
                <f7-badge
                  :color="
                    participant.status === 'ESCROWED' ? 'green' : 'orange'
                  "
                >
                  {{ participant.status }}
                </f7-badge>
              </template>
            </f7-list-item>
          </f7-list>

          <f7-block strong inset class="cart-actions-block">
            <f7-button fill round raisedlarge class="action-btn" @click="copyLink">{{
              $t("sharedCart.copyLink")
            }}</f7-button>
            <f7-button outline round raised large class="action-btn" @click="invite">{{
              $t("sharedCart.inviteParticipant")
            }}</f7-button>
            <f7-button
              fill raised round
              color="green"
              large
              class="action-btn"
              @click="checkout"
              >{{ $t("sharedCart.reservePayment") }}</f7-button
            >
          </f7-block>
        </template>
      </template>
    </AppLayout>
  </f7-page>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { f7 } from "framework7-vue";
import AppLayout from "@/components/common/AppLayout.vue";
import { shopApi } from "@/services/shopApi";
import { useAuthStore } from "@/js/pinia-stores/auth.js";
import i18n from "@/js/i18n.js";

const props = defineProps({
  cartId: { type: String, default: "" },
  f7route: Object,
  f7router: Object,
});
const authStore = useAuthStore();
const { t } = i18n.global;

const cart = ref({
  items: [],
  participants: [],
  currency: "EUR",
  progress: 50,
});
const cartItems = ref([]);
const loading = ref(true);
const error = ref("");

const participantsCount = computed(
  () => (cart.value.participants?.length || 0) + (cartItems.value?.length || 0),
);
const totalPrice = computed(() =>
  cartItems.value.reduce(
    (sum, item) =>
      sum +
      (Number(item.totalAmount) ||
        (Number(item.currentUnitPrice) || 0) * (Number(item.quantity) || 0)),
    0,
  ),
);

onMounted(load);

async function load() {
  loading.value = true;
  error.value = "";
  try {
    const targetCartId = props.cartId || (await shopApi.getSharedCart()).cartId;
    if (!targetCartId) return;
    const [cartResult, itemsResult] = await Promise.all([
      shopApi.getCart(targetCartId),
      shopApi.getCartItems(targetCartId),
    ]);
    cart.value = { ...cart.value, ...cartResult };
    cartItems.value = itemsResult.items || cartResult.items || [];
  } catch (err) {
    error.value = err.message || t("sharedCart.errorLoad");
  } finally {
    loading.value = false;
  }
}

async function copyLink() {
  const targetCartId = props.cartId || cart.value.id || "demo";
  await navigator.clipboard.writeText(
    `${location.origin}/shared-cart/${targetCartId}`,
  );
  f7.toast.show({ text: t("sharedCart.linkCopied"), closeTimeout: 1500 });
}

async function invite() {
  const email = window.prompt(t("sharedCart.participantEmailPrompt"));
  if (email) {
    const targetCartId = props.cartId || cart.value.id || "demo";
    await shopApi.invite(targetCartId, { email });
    f7.toast.show({ text: t('sharedCart.invitationSent'), closeTimeout: 1500 });
  }
}

async function checkout() {
  const targetCartId = props.cartId || cart.value.id || "demo";
  await shopApi.checkout(targetCartId);
  f7.dialog.alert(
    t("sharedCart.paymentReservedText"),
    t("sharedCart.paymentReserved"),
  );
  await load();
}

function formatMoney(value) {
  if (value == null) return "";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: cart.value.currency || "EUR",
  }).format(value);
}
</script>

<style scoped>
.cart-summary-block {
  background: color-mix(in srgb, var(--f7-theme-color) 4%, transparent);
}
.remaining-text {
  font-size: 12.5px;
  color: var(--f7-on-surface-variant, #666);
  margin-top: 8px;
}
.cart-actions-block {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.action-btn {
  margin: 0;
}
</style>
